import { ArrowRightLeft } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Combobox,
  CurrencyInput,
  DatePicker,
  Input,
  IconButton,
  Modal,
  type CivilDate,
  formatCivilDate,
  formatCurrency,
} from '@hue/design-system';
import { api, getApiError, type Currency } from '../../lib/api.js';
import { Pagination, RequestAlert, ListFilterBar } from '../../app/data-view.js';

interface Account {
  id: string;
  displayName: string;
  businessUnitName: string;
  currency: Currency;
}
interface Candidate {
  id: string;
  accountName: string;
  businessUnitName: string;
  occurredOn: string;
  description: string;
  amountCents: number;
  currency: Currency;
}
interface Candidates {
  candidates: Candidate[];
  total: number;
}

async function refreshTransfers(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.invalidateQueries({ queryKey: ['company'] });
}

export function TransferDialog({
  sourceAccountId,
  today,
  onClose,
}: {
  sourceAccountId: string;
  today: CivilDate;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const accounts = useQuery({
    queryKey: ['company', 'transfer-accounts'],
    queryFn: async () =>
      (await api.get<{ accounts: Account[] }>('/tenant/financial-transfers/accounts')).data
        .accounts,
  });
  const [source, setSource] = useState(sourceAccountId);
  const [destination, setDestination] = useState('');
  const [day, setDay] = useState<CivilDate | null>(today);
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('Transferência entre contas próprias');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const currency = accounts.data?.find((a) => a.id === source)?.currency ?? 'BRL';
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      await api.post('/tenant/financial-transfers', {
        sourceAccountId: source,
        destinationAccountId: destination,
        occurredOn: day,
        amountCents: amount,
        description,
      });
      await refreshTransfers(queryClient);
      onClose();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Transferir entre contas"
      description="Registra a saída e a entrada juntas, inclusive entre unidades. Se já importou os dois extratos, vincule os movimentos na Conciliação."
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Registrar transferência
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <TransferChoice
          label="Conta de origem"
          value={source}
          onChange={(value) => {
            setSource(value);
            setDestination('');
          }}
          options={(accounts.data ?? []).map((a) => ({
            value: a.id,
            label: `${a.displayName} · ${a.businessUnitName}`,
          }))}
        />
        <TransferChoice
          label="Conta de destino"
          value={destination}
          onChange={setDestination}
          options={(accounts.data ?? [])
            .filter((a) => a.id !== source && a.currency === currency)
            .map((a) => ({ value: a.id, label: `${a.displayName} · ${a.businessUnitName}` }))}
        />
        <DatePicker
          label="Data da transferência"
          value={day}
          onValueChange={setDay}
          today={today}
        />
        <CurrencyInput
          label="Valor da transferência"
          currency={currency}
          value={amount}
          onValueChange={(value) => setAmount(value ?? 0)}
        />
        <Input
          label="Descrição da transferência"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={240}
        />
        {accounts.error ? <RequestAlert message={getApiError(accounts.error)} /> : null}
        {error ? <RequestAlert message={error} /> : null}
      </div>
    </Modal>
  );
}

/// A consulta acontece ao exibir a linha; importar o segundo extrato faz a sugestão surgir sem
/// depender de regra de texto específica da Ton ou do Inter.
export function TransferSuggestion({
  movementId,
  canSettle,
}: {
  movementId: string;
  canSettle: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const candidates = useQuery({
    queryKey: ['company', 'transfer-candidates', movementId, search, offset],
    retry: false,
    queryFn: async () =>
      (
        await api.get<Candidates>(`/tenant/financial-transfers/candidates/${movementId}`, {
          params: { search, offset },
        })
      ).data,
  });
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      await api.post('/tenant/financial-transfers/links', { movementIds: [movementId, selected] });
      await refreshTransfers(queryClient);
      setOpen(false);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  if (!open && !candidates.data?.total) return null;
  return (
    <>
      <p className="mt-3 text-sm text-content-muted">
        Possível transferência: mesmo valor em outra conta, com até três dias de diferença.
      </p>
      <IconButton
        label="Conferir transferência"
        icon={<ArrowRightLeft />}
        onClick={() => setOpen(true)}
      />
      {open ? (
        <Modal
          open
          onOpenChange={setOpen}
          title="Vincular transferência"
          description="Confira a outra ponta. Vincular não cria novos movimentos."
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Voltar
              </Button>
              <Button
                disabled={!selected || !canSettle}
                loading={saving}
                onClick={() => void save()}
              >
                Confirmar vínculo
              </Button>
            </div>
          }
        >
          <ListFilterBar
            variant="panel"
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setOffset(0);
              setSelected('');
            }}
            searchLabel="Buscar a outra ponta"
            count={`${candidates.data?.total ?? 0} ${candidates.data?.total === 1 ? 'movimento' : 'movimentos'}`}
          />
          <TransferChoice
            label="Movimento da outra conta"
            value={selected}
            onChange={setSelected}
            options={(candidates.data?.candidates ?? []).map((c) => ({
              value: c.id,
              label: `${c.accountName} · ${c.businessUnitName} · ${formatCivilDate(c.occurredOn)} · ${formatCurrency(c.amountCents, c.currency)} · ${c.description}`,
            }))}
          />
          {candidates.data?.candidates
            .filter((candidate) => candidate.id === selected)
            .map((candidate) => (
              <p key={candidate.id} className="mt-3 break-words text-sm text-content-muted">
                {candidate.accountName} · {candidate.businessUnitName} ·{' '}
                {formatCivilDate(candidate.occurredOn)} ·{' '}
                {formatCurrency(candidate.amountCents, candidate.currency)}
                <br />
                {candidate.description}
              </p>
            ))}
          <Pagination
            total={candidates.data?.total ?? 0}
            limit={20}
            offset={offset}
            onOffsetChange={(value) => {
              setOffset(value);
              setSelected('');
            }}
            itemLabel={(count) => (count === 1 ? 'movimento' : 'movimentos')}
          />
          {error ? <RequestAlert message={error} /> : null}
          {candidates.error ? <RequestAlert message={getApiError(candidates.error)} /> : null}
        </Modal>
      ) : null}
    </>
  );
}

export function TransferChoice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <Combobox
      label={label}
      placeholder="Escolha uma opção"
      selectedLabel={options.find((option) => option.value === value)?.label ?? ''}
      options={options}
      onClear={() => onChange('')}
      onSelect={(choice) => {
        if (choice.kind === 'option') onChange(choice.value);
      }}
    />
  );
}
